import React, { useEffect, useMemo, useState } from "react";
import { useActiveBusiness, usePayLinks } from "../hooks/query/dposQueries";
import { Skeleton } from "./ui/skeleton";
import {
  ArrowRight,
  Check,
  Clock,
  Copy,
  Mail,
  MessageSquare,
  Search,
  Share2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SecuredFooter } from "./ui/secured-footer";
import paymentLinksHeroIllustration from "../assets/images/businessSuite/paylinkDefault.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { cn } from "../lib/utils";
import filterIcon from "../assets/images/trading/filter.svg";
import sortIcon from "../assets/images/trading/sort.svg";
import payLinkIcon from "../assets/images/trading/paylink.svg";
import { useToast } from "../hooks/use-toast";
import whatsappIcon from "../assets/images/support/whatsapp.png";
import type {
  DatePreset,
  PaymentLinkRow,
  PaymentLinkStatus,
  SortMode,
  SplitPaymentLaunchContext,
} from "../types/paymentLinksPanel";
import {
  filterAndSortPaymentLinks,
  resolvePaymentLinkShareUrl,
} from "../utils/paymentLinksPanelUtils";
import {
  NewPayLinkWizard,
  type NewPayLinkWizardStep,
  type PayLinkCryptoAsset,
} from "./NewPayLinkWizard";

export type {
  PaymentLinkRow,
  PaymentLinkStatus,
  SplitPaymentLaunchContext,
} from "../types/paymentLinksPanel";

export interface PaymentLinksPanelProps {
  onGetStarted?: () => void;
  paymentLinks?: PaymentLinkRow[] | null;
  onCancelPaymentLink?: (row: PaymentLinkRow) => void | Promise<void>;
  launchPayLinkWizard?: boolean;
  splitPaymentLaunchContext?: SplitPaymentLaunchContext | null;
  onLaunchPayLinkWizardConsumed?: () => void;
}

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "amount-asc", label: "Lowest to highest amount" },
  { value: "amount-desc", label: "Highest to lowest amount" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

const DATE_RANGE_OPTIONS: {
  value: Exclude<DatePreset, "custom">;
  label: string;
}[] = [
  { value: "all", label: "All time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
];

function StatusBadge({ status }: { status: PaymentLinkStatus }) {
  if (status === "paid") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-[#E8F9F6] px-2.5 py-1 text-xs font-semibold text-[#0E7563]"
        data-status="paid"
      >
        <div className="w-[16px] h-[16px] bg-[#17C3A5] rounded-full flex items-center justify-center">
          <Check
            className="h-[10px] w-[10px] shrink-0 text-white"
            aria-hidden
          />
        </div>
        PAID
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-[#F0F0F3] px-2.5 py-1 text-xs font-semibold text-[#6D6584]"
        data-status="expired"
      >
        <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        EXPIRED
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[#FFF2EB] px-2.5 py-1 text-xs font-semibold text-[#FB8137]"
      data-status="unpaid"
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
      UNPAID
    </span>
  );
}

function paymentLinkShareUrl(row: PaymentLinkRow): string {
  if (row.shareUrl) {
    return row.shareUrl;
  }
  if (typeof window === "undefined") {
    return "";
  }
  return resolvePaymentLinkShareUrl(row, window.location.origin);
}

function paymentLinkShareBody(row: PaymentLinkRow, url: string): string {
  return `Payment link for ${row.name} — ${row.amountFormatted}\n${row.reference}\n${url}`;
}

const shareMenuGradientIcon =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#57249C] to-[#EB3366] text-white";

type ShareMenuItem = {
  id: string;
  label: string;
  leadingClassName: string;
  leading: React.ReactNode;
  onClick: () => void | Promise<void>;
};

function PaymentLinkSharePopover({ row }: { row: PaymentLinkRow }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const shareMenuItems = useMemo((): ShareMenuItem[] => {
    const close = () => setOpen(false);
    return [
      {
        id: "whatsapp",
        label: "Send via Whatsapp",
        leadingClassName:
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm",
        leading: (
          <img
            src={whatsappIcon}
            alt="WhatsApp"
            className="w-[40px] h-[40px] object-contain"
          />
        ),
        onClick: () => {
          close();
          const url = paymentLinkShareUrl(row);
          const body = paymentLinkShareBody(row, url);
          window.open(
            `https://wa.me/?text=${encodeURIComponent(body)}`,
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      {
        id: "email",
        label: "Send via Email",
        leadingClassName: shareMenuGradientIcon,
        leading: <Mail className="h-5 w-5" strokeWidth={2} aria-hidden />,
        onClick: () => {
          close();
          const url = paymentLinkShareUrl(row);
          const body = paymentLinkShareBody(row, url);
          const subject = encodeURIComponent(`Payment link — ${row.name}`);
          window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(body)}`;
        },
      },
      {
        id: "sms",
        label: "Send via SMS",
        leadingClassName: shareMenuGradientIcon,
        leading: (
          <MessageSquare className="h-5 w-5" strokeWidth={2} aria-hidden />
        ),
        onClick: () => {
          close();
          const url = paymentLinkShareUrl(row);
          const body = paymentLinkShareBody(row, url);
          window.location.href = `sms:?body=${encodeURIComponent(body)}`;
        },
      },
      {
        id: "copy",
        label: "Copy link",
        leadingClassName: shareMenuGradientIcon,
        leading: <Copy className="h-5 w-5" strokeWidth={2} aria-hidden />,
        onClick: async () => {
          close();
          const url = paymentLinkShareUrl(row);
          try {
            await navigator.clipboard.writeText(url);
            toast({ description: "Link copied to clipboard" });
          } catch {
            toast({
              description: "Could not copy link",
              variant: "destructive",
            });
          }
        },
      },
      {
        id: "native",
        label: "Share",
        leadingClassName: shareMenuGradientIcon,
        leading: <Share2 className="h-5 w-5" strokeWidth={2} aria-hidden />,
        onClick: async () => {
          close();
          const url = paymentLinkShareUrl(row);
          const body = paymentLinkShareBody(row, url);
          if (!navigator.share) {
            try {
              await navigator.clipboard.writeText(url);
              toast({ description: "Link copied to clipboard" });
            } catch {
              toast({
                description: "Could not copy link",
                variant: "destructive",
              });
            }
            return;
          }
          try {
            await navigator.share({
              title: "Payment link",
              text: body,
              url,
            });
          } catch {
            /* user dismissed share sheet */
          }
        },
      },
    ];
  }, [row, toast]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full border-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] text-white hover:opacity-90"
          aria-label={`Share payment link for ${row.name}`}
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(100vw-2rem,320px)] rounded-2xl border border-[#EBE9EF] bg-white shadow-lg p-3"
      >
        <ul className="flex flex-col">
          {shareMenuItems.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-gray-50"
                onClick={() => void item.onClick()}
              >
                <span className={item.leadingClassName}>{item.leading}</span>
                <span className="min-w-0 flex-1 text-[16px] font-semibold text-[#423E51]">
                  {item.label}
                </span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF]">
                  <ArrowRight className="h-4 w-4 text-[#6D6584]" aria-hidden />
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function PaymentLinksListView({
  links,
  businessId,
  fallbackBusinessName,
  onGetStarted,
  onCancelPaymentLink,
  launchPayLinkWizard,
  splitPaymentLaunchContext,
  onLaunchPayLinkWizardConsumed,
}: {
  links: PaymentLinkRow[] | null;
  businessId: string | null;
  fallbackBusinessName?: string | null;
  onGetStarted?: () => void;
  onCancelPaymentLink?: (row: PaymentLinkRow) => void | Promise<void>;
  launchPayLinkWizard?: boolean;
  splitPaymentLaunchContext?: SplitPaymentLaunchContext | null;
  onLaunchPayLinkWizardConsumed?: () => void;
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [appliedPreset, setAppliedPreset] = useState<DatePreset>("all");
  const [appliedCustomFrom, setAppliedCustomFrom] = useState("");
  const [appliedCustomTo, setAppliedCustomTo] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<
    Exclude<DatePreset, "custom"> | "custom"
  >("all");
  const [pendingCustomFrom, setPendingCustomFrom] = useState("");
  const [pendingCustomTo, setPendingCustomTo] = useState("");
  const [showCustomRangeFields, setShowCustomRangeFields] = useState(false);
  const [showListAfterGetStarted, setShowListAfterGetStarted] = useState(false);
  const [payLinkWizardStep, setPayLinkWizardStep] =
    useState<NewPayLinkWizardStep | null>(null);
  const [plFirstName, setPlFirstName] = useState("");
  const [plLastName, setPlLastName] = useState("");
  const [plReference, setPlReference] = useState("");
  const [plSelectedAsset, setPlSelectedAsset] =
    useState<PayLinkCryptoAsset | null>(null);
  const [plAmount, setPlAmount] = useState("");
  const [wizardSplitContext, setWizardSplitContext] =
    useState<SplitPaymentLaunchContext | null>(null);

  useEffect(() => {
    if (!launchPayLinkWizard) {
      return;
    }
    setWizardSplitContext(splitPaymentLaunchContext ?? null);
    setShowListAfterGetStarted(true);
    setPayLinkWizardStep("form");
    onLaunchPayLinkWizardConsumed?.();
  }, [
    launchPayLinkWizard,
    splitPaymentLaunchContext,
    onLaunchPayLinkWizardConsumed,
  ]);

  const closePayLinkWizard = () => {
    setPayLinkWizardStep(null);
    setPlFirstName("");
    setPlLastName("");
    setPlReference("");
    setPlSelectedAsset(null);
    setPlAmount("");
    setWizardSplitContext(null);
  };

  const isFilterActive =
    appliedPreset === "custom"
      ? Boolean(appliedCustomFrom && appliedCustomTo)
      : appliedPreset !== "all";

  const isSortActive = sortMode !== "newest";

  const baseLinks = useMemo(() => (Array.isArray(links) ? links : []), [links]);

  const filtered = useMemo(
    () =>
      filterAndSortPaymentLinks({
        rows: baseLinks,
        query,
        sortMode,
        appliedPreset,
        appliedCustomFrom,
        appliedCustomTo,
      }),
    [
      baseLinks,
      query,
      sortMode,
      appliedPreset,
      appliedCustomFrom,
      appliedCustomTo,
    ]
  );

  const emptyListMessage =
    filtered.length > 0
      ? null
      : query.trim()
        ? "No payment links match your search."
        : baseLinks.length === 0
          ? "No payment links yet."
          : "No payment links match your filters.";

  const hasNoLinks =
    links === null || (Array.isArray(links) && links.length === 0);
  const showEmptyHero =
    hasNoLinks && !isFilterActive && !isSortActive && !showListAfterGetStarted;

  if (showEmptyHero) {
    return (
      <PaymentLinksEmptyState
        onGetStarted={() => {
          onGetStarted?.();
          setShowListAfterGetStarted(true);
        }}
      />
    );
  }

  if (payLinkWizardStep !== null) {
    return (
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-1 flex-col lg:pt-8 pt-5 lg:px-8 px-5">
          <NewPayLinkWizard
            step={payLinkWizardStep}
            onStepChange={setPayLinkWizardStep}
            onClose={closePayLinkWizard}
            businessId={businessId}
            fallbackBusinessName={fallbackBusinessName}
            firstName={plFirstName}
            onFirstNameChange={setPlFirstName}
            lastName={plLastName}
            onLastNameChange={setPlLastName}
            reference={plReference}
            onReferenceChange={setPlReference}
            selectedAsset={plSelectedAsset}
            onSelectAsset={setPlSelectedAsset}
            amount={plAmount}
            onAmountChange={setPlAmount}
            splitPaymentContext={wizardSplitContext}
          />
        </div>
      </TooltipProvider>
    );
  }

  const syncPendingFromApplied = () => {
    setPendingPreset(appliedPreset === "custom" ? "custom" : appliedPreset);
    setPendingCustomFrom(appliedCustomFrom);
    setPendingCustomTo(appliedCustomTo);
    setShowCustomRangeFields(appliedPreset === "custom" ? true : false);
  };

  const handleFilterOpenChange = (open: boolean) => {
    setFilterOpen(open);
    if (open) {
      syncPendingFromApplied();
    }
  };

  const handleApplyDateFilter = () => {
    if (pendingPreset === "custom" && pendingCustomFrom && pendingCustomTo) {
      setAppliedPreset("custom");
      setAppliedCustomFrom(pendingCustomFrom);
      setAppliedCustomTo(pendingCustomTo);
    } else {
      setAppliedPreset(pendingPreset === "custom" ? "all" : pendingPreset);
      setAppliedCustomFrom("");
      setAppliedCustomTo("");
    }
    setFilterOpen(false);
  };

  const handleResetDateFilter = () => {
    setPendingPreset("all");
    setPendingCustomFrom("");
    setPendingCustomTo("");
    setShowCustomRangeFields(false);
    setAppliedPreset("all");
    setAppliedCustomFrom("");
    setAppliedCustomTo("");
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-1 flex-col lg:pt-8 pt-5 lg:px-8 px-5">
        <div className="border-b border-gray-100">
          <h2 className="text-[20px] font-semibold text-[#6D6584] shrink-0 mb-7">
            Payment links
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:flex-1 lg:justify-between mb-7">
            <div className="relative min-w-0 flex-1 sm:max-w-md">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-[16px] w-[16px] -translate-y-1/2 text-[#6D6584]"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-[56px] rounded-full bg-[#EBE9EF4D] pl-10 pr-4 border-none text-[#6D6584] text-[14px]"
                aria-label="Search payment links"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Popover open={filterOpen} onOpenChange={handleFilterOpenChange}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2",
                      isFilterActive && "border-[#EB3366] bg-[#EB3366]/5"
                    )}
                  >
                    <span>Filter</span>
                    <img
                      src={filterIcon}
                      alt=""
                      className="w-[16px] h-[16px] text-[#6D6584]"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-[min(100vw-2rem,280px)] rounded-2xl border border-[#EBE9EF] bg-white p-0 shadow-lg"
                >
                  <div className="max-h-[min(70vh,420px)] overflow-y-auto px-3 pt-3 pb-2">
                    <div className="flex flex-col gap-0.5">
                      {DATE_RANGE_OPTIONS.map((opt) => {
                        const selected =
                          pendingPreset !== "custom" &&
                          pendingPreset === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => {
                              setPendingPreset(opt.value);
                              setShowCustomRangeFields(false);
                            }}
                            className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-2.5 text-left text-sm outline-none transition-colors hover:bg-gray-50"
                          >
                            <span
                              className={cn(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                                selected
                                  ? "border-[#EB3366] bg-[#EB3366]"
                                  : "border-gray-300 bg-white"
                              )}
                              aria-hidden
                            >
                              {selected ? (
                                <span className="h-2 w-2 rounded-full bg-white" />
                              ) : null}
                            </span>
                            <span
                              className={cn(
                                selected
                                  ? "font-semibold text-gray-900"
                                  : "font-normal text-gray-500"
                              )}
                            >
                              {opt.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingPreset("custom");
                        setShowCustomRangeFields(true);
                      }}
                      className={cn(
                        "mt-2 w-full rounded-full border border-[#EBE9EF] bg-white px-4 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50",
                        pendingPreset === "custom" &&
                          "border-[#EB3366] bg-[#EB3366]/5"
                      )}
                    >
                      Custom date range
                    </button>
                    {showCustomRangeFields || pendingPreset === "custom" ? (
                      <div className="mt-3 flex flex-col gap-2">
                        <label className="sr-only" htmlFor="paylink-from">
                          From date
                        </label>
                        <input
                          id="paylink-from"
                          type="date"
                          value={pendingCustomFrom}
                          onChange={(e) => setPendingCustomFrom(e.target.value)}
                          className="h-10 w-full rounded-xl border border-[#EBE9EF] px-3 text-sm text-gray-800"
                        />
                        <label className="sr-only" htmlFor="paylink-to">
                          To date
                        </label>
                        <input
                          id="paylink-to"
                          type="date"
                          value={pendingCustomTo}
                          onChange={(e) => setPendingCustomTo(e.target.value)}
                          className="h-10 w-full rounded-xl border border-[#EBE9EF] px-3 text-sm text-gray-800"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="border-t border-[#EBE9EF] px-3 py-3 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 flex-1 rounded-full border-[#EBE9EF] bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      onClick={handleResetDateFilter}
                    >
                      Reset all
                    </Button>
                    <Button
                      type="button"
                      className="h-10 flex-1 rounded-full border-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] text-sm font-semibold text-white hover:opacity-90"
                      onClick={handleApplyDateFilter}
                    >
                      Apply
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2",
                      isSortActive && "border-[#EB3366] bg-[#EB3366]/5"
                    )}
                  >
                    <span>Sort</span>
                    <img
                      src={sortIcon}
                      alt=""
                      className="w-[16px] h-[16px] text-[#6D6584]"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[min(100vw-2rem,280px)] rounded-2xl border border-[#EBE9EF] bg-white p-2 shadow-lg"
                >
                  {SORT_OPTIONS.map((opt) => {
                    const selected = sortMode === opt.value;
                    return (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => setSortMode(opt.value)}
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm focus:bg-gray-50"
                      >
                        <span
                          className={cn(
                            selected
                              ? "font-semibold text-gray-900"
                              : "font-normal text-gray-500"
                          )}
                        >
                          {opt.label}
                        </span>
                        {selected ? (
                          <Check
                            className="h-4 w-4 shrink-0 text-[#EB3366]"
                            aria-hidden
                          />
                        ) : (
                          <span className="h-4 w-4 shrink-0" aria-hidden />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                className="h-11 min-w-[133px] rounded-full border-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] px-5 text-sm font-semibold text-white"
                onClick={() => {
                  setWizardSplitContext(null);
                  setPayLinkWizardStep("form");
                }}
              >
                New pay link
              </Button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 min-h-[500px] max-h-[600px] overflow-y-auto mb-5">
          <ul className="px-10">
            {filtered.map((row, index) => (
              <li key={`${row.id}-${index}`} className="py-4 first:pt-2 mb-5">
                <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
                  <div className="flex items-center min-w-0 items-start gap-3 lg:w-[25%]">
                    <span
                      className="inline-block h-6 w-6 shrink-0 bg-[#6D6584]"
                      style={{
                        maskImage: `url(${payLinkIcon})`,
                        WebkitMaskImage: `url(${payLinkIcon})`,
                        maskSize: "contain",
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                      }}
                      role="img"
                      aria-label="Payment link icon"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#423E51] mb-[4px] text-[16px]">
                        {row.name}
                      </p>
                      <p className="text-[14px] text-[#6D6584]">
                        Created: {row.createdLabel}
                      </p>
                    </div>
                  </div>
                  <div className="min-w-0 lg:w-[18%]">
                    <p className="text-[14px] text-[#6D6584] mb-[4px]">
                      Amount requested
                    </p>
                    <p className="font-semibold text-[16px] text-[#423E51]">
                      {row.amountFormatted}
                    </p>
                  </div>
                  <div className="min-w-0 lg:w-[30%]">
                    <p className="text-[14px] text-[#6D6584] mb-[4px]">
                      Reference
                    </p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="truncate font-semibold text-[#423E51] text-[16px] cursor-default">
                          {row.reference}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-xs rounded-full border border-[#EBE9EF] bg-white px-4 py-2 text-sm font-normal text-gray-800 shadow-md"
                      >
                        {row.reference}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex min-w-0 items-center lg:w-[13%] lg:justify-center">
                    <StatusBadge status={row.status} />
                  </div>
                  <div className="flex min-w-0 shrink-0 items-center justify-end gap-2 lg:w-[13%]">
                    {row.status === "paid" ? (
                      <span
                        className="inline-flex h-10 w-[88px] shrink-0"
                        aria-hidden
                      />
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0 rounded-full border-gray-200"
                          aria-label={`Cancel payment link for ${row.name}`}
                          onClick={() => {
                            void (async () => {
                              if (onCancelPaymentLink) {
                                try {
                                  await onCancelPaymentLink(row);
                                } catch {
                                  toast({
                                    description:
                                      "Could not cancel this payment link. Try again.",
                                    variant: "destructive",
                                  });
                                }
                              } else {
                                toast({
                                  description:
                                    "Cancel link is not available yet. Please try again later.",
                                });
                              }
                            })();
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-gray-600" />
                        </Button>
                        <PaymentLinkSharePopover row={row} />
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {emptyListMessage ? (
            <p className="py-12 text-center text-sm text-gray-500">
              {emptyListMessage}
            </p>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );
}

function PaymentLinksLoadingSkeleton() {
  return (
    <div
      className="flex flex-1 flex-col lg:pt-8 pt-5 lg:px-8 px-5"
      aria-busy
      aria-label="Loading payment links"
    >
      <Skeleton className="mb-7 h-7 w-40 rounded-lg bg-[#EBE9EF]" />
      <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Skeleton className="h-14 w-full max-w-md rounded-full bg-[#EBE9EF]" />
        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-12 w-[107px] rounded-full bg-[#EBE9EF]" />
          <Skeleton className="h-12 w-[107px] rounded-full bg-[#EBE9EF]" />
          <Skeleton className="h-11 w-[133px] rounded-full bg-[#EBE9EF]" />
        </div>
      </div>
      <div className="space-y-6 px-4">
        {[0, 1, 2].map((k) => (
          <div
            key={k}
            className="flex flex-col gap-3 lg:flex-row lg:items-center"
          >
            <Skeleton className="h-16 flex-1 rounded-xl bg-[#EBE9EF]" />
            <Skeleton className="h-12 w-32 rounded-xl bg-[#EBE9EF] lg:w-[18%]" />
            <Skeleton className="h-12 flex-1 rounded-xl bg-[#EBE9EF] lg:w-[30%]" />
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentLinksErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center px-6 py-14 text-center">
      <h2 className="text-[20px] font-semibold text-[#33125F]">
        Could not load payment links
      </h2>
      <p className="mt-3 max-w-md text-[14px] text-[#423E51]">
        Check your connection and try again.
      </p>
      <Button
        type="button"
        className="mt-6 rounded-full border-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] px-8 py-5 text-sm font-semibold text-white hover:opacity-90"
        onClick={() => onRetry()}
      >
        Retry
      </Button>
    </div>
  );
}

function PaymentLinksEmptyState({
  onGetStarted,
}: {
  onGetStarted?: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-14 min-h-[700px] text-center">
      <img
        src={paymentLinksHeroIllustration}
        alt="Payment links hero illustration"
        className="w-[194px] h-[136px]"
      />
      <h2 className="text-[24px] font-semibold text-[#33125F]">
        Payment links
      </h2>
      <p className="mt-4 max-w-md text-[14px] leading-relaxed text-[#423E51]">
        Accepting Card Payments from anywhere in the world
      </p>
      <Button
        type="button"
        className="mt-6 rounded-full border-0 bg-gradient-to-r from-[#57249C] to-[#EB3366] px-12 py-6 text-base font-semibold text-white shadow-none hover:opacity-90 min-w-[302px] h-[48px]"
        onClick={onGetStarted}
      >
        Get started
      </Button>
    </div>
  );
}

export default function PaymentLinksPanel({
  onGetStarted,
  paymentLinks,
  onCancelPaymentLink,
  launchPayLinkWizard,
  splitPaymentLaunchContext,
  onLaunchPayLinkWizardConsumed,
}: PaymentLinksPanelProps) {
  const footer = <SecuredFooter className="mt-0 border-t border-gray-100" />;
  const shellClass =
    "flex min-h-[480px] flex-col overflow-hidden rounded-lg bg-white";

  const fetchFromApi = paymentLinks === undefined;
  const {
    businessId,
    business,
    isLoading: businessLoading,
  } = useActiveBusiness();
  const {
    data: payLinksData,
    isLoading: payLinksLoading,
    isError: payLinksError,
    refetch: refetchPayLinks,
  } = usePayLinks(businessId, { enabled: fetchFromApi });

  if (fetchFromApi) {
    if (businessLoading || payLinksLoading) {
      return (
        <div className={shellClass}>
          <PaymentLinksLoadingSkeleton />
          {footer}
        </div>
      );
    }
    if (payLinksError) {
      return (
        <div className={shellClass}>
          <PaymentLinksErrorState onRetry={() => void refetchPayLinks()} />
          {footer}
        </div>
      );
    }
  }

  const linksForList: PaymentLinkRow[] | null = fetchFromApi
    ? (payLinksData?.rows ?? [])
    : Array.isArray(paymentLinks)
      ? paymentLinks
      : null;

  return (
    <div className={shellClass}>
      <PaymentLinksListView
        links={linksForList}
        businessId={businessId}
        fallbackBusinessName={business?.businessName ?? null}
        onGetStarted={onGetStarted}
        onCancelPaymentLink={onCancelPaymentLink}
        launchPayLinkWizard={launchPayLinkWizard}
        splitPaymentLaunchContext={splitPaymentLaunchContext}
        onLaunchPayLinkWizardConsumed={onLaunchPayLinkWizardConsumed}
      />
      {footer}
    </div>
  );
}
